import {AUTO_MODEL_ID} from './auto-model'
import {type CopilotChatModel, CopilotPlan, type RequiredPlan} from './copilot-chat-types'
import {isCustomModel} from './models'

/**
 * Represents a group of custom models with a display name and owner type
 */
export interface CustomModelGroup {
  displayName: string
  ownerType?: 'organization' | 'enterprise'
  models: CopilotChatModel[]
}

/**
 * Model tier configuration
 */
export interface ModelTierConfig {
  lowTier: CopilotChatModel[]
  midTier: CopilotChatModel[]
  highTier: CopilotChatModel[]
  base: CopilotChatModel[]
  preview: CopilotChatModel[]
  custom: {[groupKey: string]: CustomModelGroup}
}

// individual-plan tiers ordered from least to most privileged. Used to infer access to a CAPI-returned model
const INDIVIDUAL_PLAN_TIERS_ASCENDING: readonly CopilotPlan[] = [
  CopilotPlan.IndividualStudent,
  CopilotPlan.IndividualPro,
  CopilotPlan.IndividualProPlus,
  CopilotPlan.IndividualMax,
]

// `business` is intentionally omitted: there's no individual self-serve upgrade path to Business,
const MIN_PLAN_FOR_REQUIRED_PLAN: Readonly<Partial<Record<RequiredPlan, CopilotPlan>>> = {
  edu: CopilotPlan.IndividualStudent,
  pro: CopilotPlan.IndividualPro,
  // eslint-disable-next-line camelcase
  pro_plus: CopilotPlan.IndividualProPlus,
  max: CopilotPlan.IndividualMax,
}

/**
 * Returns the minimum individual plan that can access `model`, or undefined if the
 * model is unrestricted or only available to org plans (Business/Enterprise). For
 * backend-curated models with `requiredPlan` set this uses the static map; otherwise
 * it's the lowest-tier individual plan listed in `restricted_to`.
 */
export function getMinimumRequiredPlan(model: CopilotChatModel): CopilotPlan | undefined {
  if (model.requiredPlan) {
    return MIN_PLAN_FOR_REQUIRED_PLAN[model.requiredPlan]
  }
  const restricted = model.billing?.restricted_to
  if (!restricted?.length) return undefined
  return INDIVIDUAL_PLAN_TIERS_ASCENDING.find(plan => restricted.includes(plan))
}

/**
 * Low tier model exceptions - models that should be in low tier despite billing multiplier
 */
const LOW_TIER_MODEL_EXCEPTIONS = ['o3-mini', 'Gemini 2.0 Flash']

/**
 * Determines if a model belongs to the low tier based on category or billing
 */
function isLowTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'lightweight'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      ((model.billing?.multiplier < 1 && model.billing?.multiplier !== 0) ||
        LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName))) ||
    false
  )
}

/**
 * Determines if a model belongs to the mid tier based on category or billing
 */
function isMidTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'versatile'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      (model.billing?.multiplier === 1 || model.billing?.multiplier === 0) &&
      !LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName)) ||
    false
  )
}

/**
 * Determines if a model belongs to the high tier based on category or billing
 */
function isHighTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'powerful'
  }

  // Fallback to billing multiplier logic
  return (model.billing && model.billing?.multiplier > 1) || false
}

/**
 * Checks if models have diversity in their categories (either by category field or billing).
 * Used by `ModelPickerDesign` to decide whether the tier-grouped layout is meaningful
 * for the current model list. When the backend returns a fully-categorized response
 * (the production case) this is always true; the flat-mode fallback handles legacy or
 * synthetic data that has no `model_picker_category` to bucket on.
 */
export function hasModelCategoryDiversity(models: CopilotChatModel[]): boolean {
  const categoryDiversity =
    models.some(m => m.model_picker_category !== undefined) &&
    new Set(models.filter(m => m.model_picker_category !== undefined).map(m => m.model_picker_category)).size > 1

  const billingDiversity =
    models.some(m => m.billing?.multiplier !== undefined) &&
    new Set(models.filter(m => m.billing?.multiplier !== undefined).map(m => m.billing?.multiplier)).size > 1

  return categoryDiversity || billingDiversity
}

/**
 * Creates a unique group key for custom models based on owner name and type.
 * This ensures enterprise and org models with the same name are in separate groups.
 */
function getCustomModelGroupKey(model: CopilotChatModel): string {
  const ownerName = model.custom_model!.owner_name
  const ownerType = model.custom_model!.owner_type ?? 'organization'
  return `${ownerName}::${ownerType}`
}

export interface CategorizeModelsOptions {
  shouldShowCategories: boolean
  shouldShowPreviewModelsSeparately?: boolean
}

/**
 * Adds "(enterprise)" or "(organization)" suffix only when there are two with the same name.
 */
function getDisplayName(
  ownerName: string,
  ownerType: 'organization' | 'enterprise' | undefined,
  allGroups: {[groupKey: string]: {ownerName: string; ownerType?: 'organization' | 'enterprise'}},
): string {
  // Check if there's another group with the same owner name but different owner type
  const hasNameCollision = Object.values(allGroups).some(
    group =>
      group.ownerType !== ownerType &&
      group.ownerName.trim().toLocaleLowerCase() === ownerName.trim().toLocaleLowerCase(),
  )

  if (hasNameCollision && ownerType) {
    return `${ownerName} (${ownerType})`
  }
  return ownerName
}

/**
 * Sorts models alphabetically in descending order by display name.
 * This ensures newer models (e.g. "Claude Opus 4.8") appear above older ones (e.g. "Claude Opus 4.7").
 */
function sortModelsDescending(models: CopilotChatModel[]): CopilotChatModel[] {
  return [...models].sort((a, b) => b.displayName.localeCompare(a.displayName))
}

/**
 * Categorizes models into tiers for the model picker
 */
export function categorizeModels(models: CopilotChatModel[], options: CategorizeModelsOptions): ModelTierConfig {
  const {shouldShowCategories, shouldShowPreviewModelsSeparately = true} = options

  const [customModels, defaultModels] = models.reduce<[CopilotChatModel[], CopilotChatModel[]]>(
    (acc, m) => {
      acc[isCustomModel(m) ? 0 : 1].push(m)
      return acc
    },
    [[], []],
  )

  const groupInfo: {[groupKey: string]: {ownerName: string; ownerType?: 'organization' | 'enterprise'}} = {}
  for (const m of customModels) {
    const groupKey = getCustomModelGroupKey(m)
    if (!groupInfo[groupKey]) {
      groupInfo[groupKey] = {
        ownerName: m.custom_model!.owner_name,
        ownerType: m.custom_model!.owner_type,
      }
    }
  }

  const customModelGroups = customModels.reduce(
    (acc, m) => {
      const groupKey = getCustomModelGroupKey(m)
      if (!acc[groupKey]) {
        const ownerName = m.custom_model!.owner_name
        const ownerType = m.custom_model!.owner_type
        acc[groupKey] = {
          displayName: getDisplayName(ownerName, ownerType, groupInfo),
          ownerType,
          models: [],
        }
      }
      acc[groupKey].models.push(m)
      return acc
    },
    {} as {[groupKey: string]: CustomModelGroup},
  )

  if (!shouldShowCategories) {
    return {
      lowTier: [],
      midTier: [],
      highTier: [],
      base: shouldShowPreviewModelsSeparately ? defaultModels.filter(m => !m.preview) : defaultModels,
      preview: shouldShowPreviewModelsSeparately ? defaultModels.filter(m => m.preview) : [],
      custom: customModelGroups,
    }
  }

  for (const group of Object.values(customModelGroups)) {
    group.models = sortModelsDescending(group.models)
  }

  // If Auto Model exists (CCA), show it in the `base` section instead of categorizing it.
  const isAutoModel = (m: CopilotChatModel): boolean => m.id === AUTO_MODEL_ID
  const baseModels = defaultModels.filter(m => isAutoModel(m))
  const categorizableModels = defaultModels.filter(m => !isAutoModel(m))

  return {
    lowTier: sortModelsDescending(categorizableModels.filter(m => isLowTierModel(m))),
    midTier: sortModelsDescending(categorizableModels.filter(m => isMidTierModel(m))),
    highTier: sortModelsDescending(categorizableModels.filter(m => isHighTierModel(m))),
    base: baseModels,
    preview: [],
    custom: customModelGroups,
  }
}
